import { apiClient } from './client';
import type {
    PublisherResultImport,
    PublisherResultImportBatch,
    PublisherResultPatientApprovalStatus,
    RejectedImportFile,
} from '@/types/publisher-result-import';
import type { ReportData } from '@/types/report';

type Wrapped<T> = { data: T };

interface CreateBatchResponse extends Wrapped<PublisherResultImportBatch> {
    rejected_files?: RejectedImportFile[];
}

/** Pull the rejection list out of the 400 the batch endpoint returns when no file survived. */
export const getRejectedImportFiles = (error: unknown): RejectedImportFile[] => {
    const rejected = (error as { response?: { data?: { rejected_files?: unknown } } } | null)
        ?.response?.data?.rejected_files;

    return Array.isArray(rejected) ? (rejected as RejectedImportFile[]) : [];
};

export interface PublisherResultImportsPagination {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
}

interface PublisherResultImportsPaginatedResponse {
    data: PublisherResultImport[];
    pagination: PublisherResultImportsPagination;
}

export interface PublisherResultImportApprovalResult {
    import: PublisherResultImport;
    report: ReportData | null;
    applied_table_ids: string[];
    skipped_duplicate_table_ids: string[];
    pending_report: boolean;
}

export const publisherResultImportsApi = {
    /**
     * Each file is validated independently, so a 201 can still carry
     * rejections: those files produced no import and the caller has to say so.
     * A 400 means nothing survived — its body carries the same list.
     */
    createBatch: async (input: {
        patient_id: string;
        source_report_id?: string | null;
        files: Array<{ key: string; original_filename: string }>;
    }): Promise<{ batch: PublisherResultImportBatch; rejectedFiles: RejectedImportFile[] }> => {
        const response = await apiClient.post<CreateBatchResponse>(
            '/publisher-result-imports/batches',
            input,
        );
        const body = response as unknown as CreateBatchResponse;

        return { batch: body.data, rejectedFiles: body.rejected_files ?? [] };
    },

    getBatch: async (batchId: string): Promise<PublisherResultImportBatch> => {
        const response = await apiClient.get<Wrapped<PublisherResultImportBatch>>(
            `/publisher-result-imports/batches/${batchId}`,
        );
        return (response as unknown as Wrapped<PublisherResultImportBatch>).data;
    },

    listPatientImports: async (
        patientId: string,
        params: { page?: number; pageSize?: number } = {},
    ): Promise<PublisherResultImportsPaginatedResponse> => {
        const { page = 1, pageSize = 10 } = params;
        const response = await apiClient.get<PublisherResultImportsPaginatedResponse>(
            `/publisher-result-imports/patients/${patientId}`,
            {
                params: {
                    page,
                    pageSize,
                },
            },
        );
        return response as unknown as PublisherResultImportsPaginatedResponse;
    },

    getPatientApprovalStatuses: async (
        patientIds: string[],
    ): Promise<PublisherResultPatientApprovalStatus[]> => {
        const response = await apiClient.post<Wrapped<PublisherResultPatientApprovalStatus[]>>(
            '/publisher-result-imports/patients/approval-status',
            {
                patient_ids: patientIds,
            },
        );
        return (response as unknown as Wrapped<PublisherResultPatientApprovalStatus[]>).data;
    },

    continueDuplicate: async (importId: string): Promise<PublisherResultImport> => {
        const response = await apiClient.post<Wrapped<PublisherResultImport>>(
            `/publisher-result-imports/${importId}/continue-duplicate`,
        );
        return (response as unknown as Wrapped<PublisherResultImport>).data;
    },

    approve: async (
        importId: string,
        input: {
            selected_table_ids: string[];
            /**
             * What the guided review decided about each AI paragraph: a text
             * replaces it, `null` approves the table without one. Tables left
             * out keep whatever the pipeline wrote.
             */
            table_narratives?: Array<{ table_id: string; narrative: string | null }>;
        },
    ): Promise<PublisherResultImportApprovalResult> => {
        const response = await apiClient.post<Wrapped<PublisherResultImportApprovalResult>>(
            `/publisher-result-imports/${importId}/approve`,
            input,
        );
        return (response as unknown as Wrapped<PublisherResultImportApprovalResult>).data;
    },

    listPendingForReport: async (reportId: string): Promise<PublisherResultImport[]> => {
        const response = await apiClient.get<Wrapped<PublisherResultImport[]>>(
            `/reports/${reportId}/pending-publisher-result-imports`,
        );
        return (response as unknown as Wrapped<PublisherResultImport[]>).data;
    },

    applyToActiveReport: async (importId: string): Promise<PublisherResultImportApprovalResult> => {
        const response = await apiClient.post<Wrapped<PublisherResultImportApprovalResult>>(
            `/publisher-result-imports/${importId}/apply-to-active-report`,
        );
        return (response as unknown as Wrapped<PublisherResultImportApprovalResult>).data;
    },

    cancel: async (importId: string): Promise<PublisherResultImport> => {
        const response = await apiClient.post<Wrapped<PublisherResultImport>>(
            `/publisher-result-imports/${importId}/cancel`,
        );
        return (response as unknown as Wrapped<PublisherResultImport>).data;
    },
};
